import { LayoutChangeEvent, StyleSheet } from 'react-native'
import { DS } from '../../../design-system'
import { useEffect, useRef, useState } from 'react'
import { useStore } from '../../../models'
import Animated from 'react-native-reanimated'
import { useFlyAndScaleAnimation } from '../../../screens/new-design/hooks/useFlyAndScaleAnimation'
import { observer } from 'mobx-react-lite'
import { PlayerCellVideo } from '../PlayerCell/PlayerCellVideo'

type Point = {
  x: number
  y: number
}

const calculateTranslation = (p1: Point, p2: Point) => {
  const translateX = p2.x - p1.x
  const translateY = p2.y - p1.y

  return {
    translateX,
    translateY
  }
}

const SMALL_VIDEO_SIZE = 48
const VIDEO_SIZE_FACTOR = 5
const VIDEO_FRAME_SIZE = SMALL_VIDEO_SIZE * VIDEO_SIZE_FACTOR

export const LiveVideoPreview = observer(() => {
  const {
    game: { playerSmallVideoPosition, setPreviewVideoWasShown },
    communication: { isLocalUserJoined, isLocalUserVideoHidden, userTappedOnGoLive }
  } = useStore()
  const viewRef = useRef(null)
  const playerBigVideoPosition = useRef<Point>({ x: 0, y: 0 })
  const [showAnimationOneTime, setShowAnimationOneTime] = useState(true)
  const showVideo = isLocalUserJoined && !isLocalUserVideoHidden && showAnimationOneTime
  const { translateX, translateY } = calculateTranslation(playerBigVideoPosition.current, playerSmallVideoPosition)

  const [animationStarted, setAnimationStarted] = useState(false)
  const [animationFinished, setAnimationFinished] = useState(false)

  useEffect(() => {
    if (userTappedOnGoLive) {
      setShowAnimationOneTime(true)
    } else {
      setShowAnimationOneTime(false)
    }
  }, [userTappedOnGoLive])

  const { animatedStyle } = useFlyAndScaleAnimation({
    animationStarted,
    applyOpacity: false,
    translateX,
    translateY,
    initialHeight: VIDEO_FRAME_SIZE,
    initialWidth: VIDEO_FRAME_SIZE,
    onFinish: () => {
      setAnimationFinished(true)
      setAnimationStarted(false)
      setPreviewVideoWasShown(true)
      setShowAnimationOneTime(false)
    }
  })

  const handleLayout = (event: LayoutChangeEvent) => {
    if (viewRef.current) {
      //@ts-ignore
      viewRef.current.measure((x, y, width, height, pageX, pageY) => {
        playerBigVideoPosition.current = { x: pageX, y: pageY }
        setAnimationStarted(true)
      })
    }
  }

  useEffect(() => {
    if (!showVideo) {
      setAnimationFinished(false)
    }
  }, [showVideo])

  if (!showVideo || animationFinished) return null

  return (
    <DS.Frame flex={1} style={[styles.container]}>
      <Animated.View ref={viewRef} style={[animatedStyle, styles.videoFrame]} onLayout={handleLayout}>
        <PlayerCellVideo
          videoStyle={{ borderRadius: VIDEO_FRAME_SIZE / 2, width: VIDEO_FRAME_SIZE, height: VIDEO_FRAME_SIZE }}
        />
      </Animated.View>
    </DS.Frame>
  )
})

export const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 100,
    alignItems: 'center',
    justifyContent: 'center'
  },
  videoFrame: {
    width: VIDEO_FRAME_SIZE,
    height: VIDEO_FRAME_SIZE,
    borderRadius: VIDEO_FRAME_SIZE / 2
  }
})
